import React, { useId } from 'react';
import { MapPin } from 'lucide-react';
import INDIAN_CITIES from '../data/indianCities';

/**
 * CitySelect — autocomplete input for Indian cities using the native HTML5 <datalist>.
 *
 * Why datalist?
 *  - Native browser support, zero JS bundle cost, native a11y + keyboard navigation
 *  - Searches case-insensitively as user types
 *  - Free-text still allowed (lets users type a small village not in the list)
 *  - Works on mobile (Chrome / Safari / Firefox / Edge)
 *
 * Props:
 *   value (string)       — controlled value
 *   onChange (string)    — fires with the chosen / typed city
 *   placeholder (string) — default 'e.g. Mumbai'
 *   required (boolean)   — make it mandatory
 *   showIcon (boolean)   — pin icon at left (default true)
 *   testId (string)      — data-testid for the input
 *   className (string)   — applies to the wrapper (default uses input-field-like styles)
 */
const CitySelect = ({
  value = '',
  onChange,
  placeholder = 'e.g. Mumbai',
  required = false,
  showIcon = true,
  testId = 'city-select',
  className = '',
  style,
}) => {
  const listId = useId();

  return (
    <div style={{ position: 'relative', width: '100%', ...style }} className={className}>
      {showIcon && (
        <span style={{
          position: 'absolute',
          left: 12, top: '50%', transform: 'translateY(-50%)',
          color: '#94A3B8', pointerEvents: 'none', display: 'flex',
        }}>
          <MapPin size={15} />
        </span>
      )}
      <input
        type="text"
        list={listId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="address-level2"
        data-testid={testId}
        className="input-field"
        style={{
          width: '100%',
          paddingLeft: showIcon ? 36 : undefined,
        }}
      />
      <datalist id={listId} data-testid={`${testId}-options`}>
        {INDIAN_CITIES.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </div>
  );
};

export default CitySelect;
